/**
 * NewTabAffordance (iter248)
 *
 * Shared pieces that let any navigation target be opened in a new BROWSER
 * tab. Used by the top nav tabs, the left sidebar rail, and the mobile
 * "More" sheet so the behaviour is identical in all three.
 *
 * Why this works at all: the auth token lives in localStorage, which is
 * shared across tabs of the same origin, so a dashboard route opened in a
 * new tab is already authenticated — no re-login.
 *
 * Three ways in, per the user's spec (a + d):
 *   1. `openInNewTab(path)`      — one plain click on the hover-revealed icon
 *   2. real `<a href>` targets   — Cmd/Ctrl+click and middle-click, native
 *   3. `NavContextMenu`          — right-click → "Open in new browser tab"
 */
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger,
} from '@/components/ui/context-menu';

export const openInNewTab = (path) => {
  window.open(path, '_blank', 'noopener,noreferrer');
};

/** True when the click should be left to the browser (new tab / new window). */
export const isModifiedClick = (e) =>
  e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;

/**
 * Plain-click button that opens `path` in a new browser tab.
 * Swallows pointerdown as well as click so it never starts a sidebar drag
 * or activates the tab underneath it.
 */
export const OpenInNewTabButton = ({ path, label, slug, className = '', iconClassName = 'h-3 w-3' }) => (
  <button
    type="button"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      openInNewTab(path);
    }}
    onPointerDown={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
    // cn() not string concatenation: callers override `rounded` and the
    // background, and plain concatenation leaves both classes in play with
    // only stylesheet order deciding the winner.
    className={cn(
      'inline-flex shrink-0 items-center justify-center rounded transition-opacity hover:brightness-125 focus:outline-none focus-visible:ring-1 focus-visible:ring-white',
      className,
    )}
    data-testid={`open-in-new-tab-${slug}`}
    aria-label={`Open ${label} in a new browser tab`}
    title={`Open ${label} in a new browser tab`}
    tabIndex={-1}
  >
    <ExternalLink className={iconClassName} />
  </button>
);

/** Right-click menu exposing the same action. */
export const NavContextMenu = ({ path, label, slug, children }) => (
  <ContextMenu>
    <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
    <ContextMenuContent data-testid={`nav-context-menu-${slug}`}>
      <ContextMenuItem
        onSelect={() => openInNewTab(path)}
        data-testid={`context-open-in-new-tab-${slug}`}
      >
        <ExternalLink className="h-3.5 w-3.5 mr-2" />
        Open {label} in a new browser tab
      </ContextMenuItem>
    </ContextMenuContent>
  </ContextMenu>
);
